import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import { Typography } from 'packages/common-frontend/components/Typography';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { SelectInput } from '~/core/routes/settings/implementations/common/Inputs';

type AddFeeModalForm = {
  tAccount: 'unusedInterestExpense';
  capitalProvider: {
    slug: string;
    name: string;
  };
};

type AddFeeModalProps = {
  modalControls: ReturnType<typeof useDisclosure>;
};

export const AddFeeModal = ({ modalControls }: AddFeeModalProps) => {
  const navigate = useNavigate();

  const form = useForm<AddFeeModalForm>({
    defaultValues: {
      tAccount: 'unusedInterestExpense',
    },
  });
  const tAccount = form.watch('tAccount');

  // todo: merge with useGetCapitalProviderOptionsByTAccount hook @markhaghani @implementation
  const capitalProviderOptions = useCreditAgreementStore((store) => {
    const takenCapitalProviders =
      store.creditAgreement?.liabilityAccounts
        ?.filter((liabilityAccount) => liabilityAccount.tAccount === tAccount)
        ?.map((liabilityAccount) => ({
          label: liabilityAccount.name,
          value: liabilityAccount.counterpartySlug,
        })) ?? [];

    return (
      store.creditAgreement?.facilityMechanics?.parties?.capitalProviders
        ?.filter(
          (capitalProvider) =>
            !takenCapitalProviders.some(
              (takenCapitalProvider) =>
                takenCapitalProvider.value === capitalProvider.entity.slug,
            ),
        )
        ?.map((capitalProvider) => ({
          key: capitalProvider.entity.id,
          label: capitalProvider.entity.name,
          value: capitalProvider.entity.slug,
        })) ?? []
    );
  });

  return (
    <Modal {...modalControls}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Fee</ModalHeader>
        <ModalCloseButton />

        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit((data) => {
              modalControls.onClose();
              if (data.capitalProvider) {
                navigate(`unusedFee/${data.capitalProvider.slug}`);
              }
            })}
          >
            <ModalBody>
              {capitalProviderOptions.length === 0 ? (
                <Alert status="info">
                  <AlertIcon />
                  <AlertDescription>
                    No available capital providers. Please delete an existing
                    capital provider or add a new one in{' '}
                    <Link as={RouterLink} to="../parties">
                      <Typography
                        inline
                        colorScheme="blue"
                        fontWeight="medium"
                        variant="base-muted"
                      >
                        parties.
                      </Typography>
                    </Link>
                  </AlertDescription>
                </Alert>
              ) : (
                <Flex gap=".5rem" direction="column">
                  <SelectInput<AddFeeModalForm>
                    label="Fee Type"
                    name="tAccount"
                    isDisabled
                    placeholder="Unused Fee"
                    options={[
                      { label: 'Unused Fee', value: 'unusedInterestExpense' },
                    ]}
                  />
                  <SelectInput<AddFeeModalForm>
                    helperText={
                      <>
                        Assign an unused fee to a capital provider by modifying
                        an existing one or adding a new capital provider in{' '}
                        <Link as={RouterLink} to="../parties">
                          <Typography
                            inline
                            colorScheme="blue"
                            fontWeight="medium"
                            variant="detail"
                          >
                            parties.
                          </Typography>
                        </Link>
                      </>
                    }
                    name="capitalProvider.slug"
                    label="Capital Provider"
                    options={capitalProviderOptions}
                    placeholder="Select a Capital Provider"
                    required
                  />
                </Flex>
              )}
            </ModalBody>

            <ModalFooter>
              <Flex gap=".5rem">
                <Button onClick={modalControls.onClose}>Cancel</Button>
                <Button
                  type="submit"
                  colorScheme="v2-aurora"
                  isDisabled={capitalProviderOptions.length === 0}
                >
                  Add
                </Button>
              </Flex>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
